import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { 
  GraduationCap, Upload, BarChart3, Brain, TrendingUp, LogOut, 
  FileText, AlertTriangle, CheckCircle, ChevronRight, Loader2, Info, Image, FileUp, Trash2, Calculator, Play, Clock
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentDashboard({ user, logout }) {
  const [stats, setStats] = useState(null);
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState("");
  const [scantronFile, setScantronFile] = useState(null);
  const [workingNotesFile, setWorkingNotesFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [studentCode, setStudentCode] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [drills, setDrills] = useState([]);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, testsRes, drillsRes] = await Promise.all([
        axios.get(`${API}/stats/overview`),
        axios.get(`${API}/tests`),
        axios.get(`${API}/drills`).catch(() => ({ data: { drills: [] } }))
      ]);
      setStats(statsRes.data);
      setTests(testsRes.data.tests || []);
      setDrills(drillsRes.data.drills || []);
      
      // Get student code for parent linking (COPPA compliant)
      try {
        const codeRes = await axios.get(`${API}/auth/student-code`);
        setStudentCode(codeRes.data.access_code || "");
      } catch (e) {
        // Not critical if this fails
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleScantronChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a JPEG, PNG, WebP image or PDF");
        return;
      }
      // FERPA: Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum 5MB allowed.");
        return;
      }
      setScantronFile(file);
    }
  };

  const handleDeleteSubmission = async (submissionId, e) => {
    e.stopPropagation(); // Prevent navigation
    setDeletingId(submissionId);
    try {
      await axios.delete(`${API}/submissions/${submissionId}`);
      toast.success("Submission deleted");
      // Refresh stats to update the list
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete submission");
    } finally {
      setDeletingId(null);
    }
  };

  const handleWorkingNotesChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a JPEG, PNG, WebP image or PDF");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum 5MB allowed.");
        return;
      }
      setWorkingNotesFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!selectedTest) {
      toast.error("Please select a test");
      return;
    }
    if (!scantronFile) {
      toast.error("Please upload your scantron/answer sheet");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("test_id", selectedTest);
      formData.append("scantron", scantronFile);
      if (workingNotesFile) {
        formData.append("working_notes", workingNotesFile);
      }

      const response = await axios.post(
        `${API}/submissions/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      // WCAG: Include text description in toast
      toast.success(
        `${response.data.percentage >= 70 ? '🎉 Great job!' : response.data.percentage >= 50 ? '👍 Good effort!' : '💪 Keep practicing!'} You scored ${response.data.score}/${response.data.total} (${response.data.percentage}%)`
      );
      setScantronFile(null);
      setWorkingNotesFile(null);
      setSelectedTest("");
      fetchData();
      navigate(`/submission/${response.data.submission_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen graph-paper-bg flex items-center justify-center" role="main">
        <div role="status" aria-live="polite" className="sr-only">Loading your dashboard</div>
        <div 
          className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"
          role="progressbar"
          aria-label="Loading"
        ></div>
      </div>
    );
  }

  const sortedWeaknesses = stats?.weaknesses 
    ? Object.entries(stats.weaknesses).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="min-h-screen graph-paper-bg">
      {/* WCAG: Skip navigation */}
      <a href="#main-content" className="skip-link">Skip to main content</a>
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40" role="banner">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center" aria-hidden="true">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                MathTrack
              </h1>
              <p className="text-sm text-gray-600">Student Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              {/* COPPA: Display anonymous name */}
              <p className="font-medium text-gray-900">{user?.display_name || "Student"}</p>
              {/* Student code for parent linking */}
              {studentCode && (
                <p className="text-xs text-gray-500" title="Share this code with your parent">
                  Code: {studentCode}
                </p>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
              aria-label="Sign out"
              tabIndex={0}
            >
              <LogOut className="w-5 h-5" aria-hidden="true" />
              <span className="sr-only">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main id="main-content" className="max-w-7xl mx-auto px-6 py-8" role="main" tabIndex={-1}>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" role="region" aria-label="Statistics summary">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="card-hover" data-testid="total-submissions-card" tabIndex={0}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Tests</p>
                    <p className="text-3xl font-bold text-gray-900 font-mono" aria-label={`${stats?.total_submissions || 0} tests taken`}>
                      {stats?.total_submissions || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" aria-hidden="true">
                    <FileText className="w-6 h-6 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="card-hover" data-testid="average-score-card" tabIndex={0}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Average Score</p>
                    <p className="text-3xl font-bold text-gray-900 font-mono" aria-label={`Average score: ${stats?.average_score || 0} percent`}>
                      {stats?.average_score || 0}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" aria-hidden="true">
                    <BarChart3 className="w-6 h-6 text-green-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="card-hover" data-testid="top-weakness-card" tabIndex={0}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Focus Area</p>
                    <p className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {sortedWeaknesses[0]?.[0] || "None yet"}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center" aria-hidden="true">
                    <AlertTriangle className="w-6 h-6 text-amber-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="card-hover" tabIndex={0}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Trend</p>
                    {/* WCAG: Text label, not just icon */}
                    <p className="text-xl font-bold text-green-700 flex items-center gap-1">
                      <TrendingUp className="w-5 h-5" aria-hidden="true" />
                      <span>Improving</span>
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" aria-hidden="true">
                    <Brain className="w-6 h-6 text-purple-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Math Drills Section */}
        {drills.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Calculator className="w-5 h-5 text-indigo-600" />
                  Practice Drills
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Timed multiplication practice assigned by your parent
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {drills.map((drill) => (
                    <div 
                      key={drill.drill_id}
                      className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">{drill.name}</h3>
                          <p className="text-xs text-gray-500">
                            Tables: {drill.tables?.slice(0, 5).join(", ")}{drill.tables?.length > 5 ? "..." : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 text-indigo-600 text-sm">
                          <Clock className="w-4 h-4" />
                          {drill.stats?.current_time_per_question || 10}s
                        </div>
                      </div>
                      
                      {drill.stats?.sessions_completed > 0 && (
                        <div className="flex items-center gap-3 mb-3 text-sm">
                          <span className="text-green-600">
                            {drill.stats.avg_accuracy}% avg
                          </span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-500">
                            {drill.stats.sessions_completed} sessions
                          </span>
                        </div>
                      )}
                      
                      <Button 
                        onClick={() => navigate(`/drills/${drill.drill_id}`)}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 gap-2"
                      >
                        <Play className="w-4 h-4" />
                        Start Drill
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Upload Section */}
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card data-testid="upload-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Upload className="w-5 h-5 text-primary" aria-hidden="true" />
                  Submit Your Answers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Select Test */}
                <div>
                  <label htmlFor="test-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Test *
                  </label>
                  <Select value={selectedTest} onValueChange={setSelectedTest}>
                    <SelectTrigger data-testid="test-select" id="test-select" aria-label="Choose a test">
                      <SelectValue placeholder="Choose a test..." />
                    </SelectTrigger>
                    <SelectContent>
                      {tests.map((test) => (
                        <SelectItem key={test.test_id || test.answer_key_id} value={test.test_id || test.answer_key_id}>
                          {test.name} - {test.grade_level} ({test.year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Scantron Upload */}
                <div>
                  <label htmlFor="scantron-input" className="block text-sm font-medium text-gray-700 mb-2">
                    Answer Sheet (Scantron) <span className="text-red-600" aria-hidden="true">*</span>
                    <span className="sr-only">(required)</span>
                  </label>
                  <label 
                    className={`dropzone ${scantronFile ? 'border-green-500 bg-green-50' : ''}`}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && document.getElementById('scantron-input').click()}
                    role="button"
                    aria-label={scantronFile ? `Selected file: ${scantronFile.name}. Press Enter to change` : "Upload answer sheet. Press Enter to browse files"}
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleScantronChange}
                      className="hidden"
                      id="scantron-input"
                      data-testid="scantron-input"
                      aria-describedby="scantron-help"
                    />
                    {scantronFile ? (
                      <div className="flex items-center gap-3 text-green-700">
                        <CheckCircle className="w-8 h-8" aria-hidden="true" />
                        <div className="text-left">
                          <p className="font-medium">{scantronFile.name}</p>
                          <p className="text-sm text-gray-600">Click or press Enter to change</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-600">
                        <Image className="w-10 h-10 mx-auto mb-3 text-gray-400" aria-hidden="true" />
                        <p className="font-medium">Drop your answer sheet here</p>
                        <p className="text-sm" id="scantron-help">Image (JPEG, PNG, WebP) or PDF - Max 5MB</p>
                      </div>
                    )}
                  </label>
                </div>

                {/* Working Notes Upload */}
                <div>
                  <label htmlFor="working-notes-input" className="block text-sm font-medium text-gray-700 mb-2">
                    Working Notes / Scratch Pad <span className="text-gray-500">(Optional)</span>
                  </label>
                  <label 
                    className={`dropzone ${workingNotesFile ? 'border-green-500 bg-green-50' : ''}`}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && document.getElementById('working-notes-input').click()}
                    role="button"
                    aria-label={workingNotesFile ? `Selected file: ${workingNotesFile.name}. Press Enter to change` : "Upload working notes (optional). Press Enter to browse files"}
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleWorkingNotesChange}
                      className="hidden"
                      id="working-notes-input"
                      data-testid="working-notes-input"
                    />
                    {workingNotesFile ? (
                      <div className="flex items-center gap-3 text-green-700">
                        <CheckCircle className="w-8 h-8" aria-hidden="true" />
                        <div className="text-left">
                          <p className="font-medium">{workingNotesFile.name}</p>
                          <p className="text-sm text-gray-600">Click or press Enter to change</p>
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-600">
                        <FileUp className="w-10 h-10 mx-auto mb-3 text-gray-400" aria-hidden="true" />
                        <p className="font-medium">Drop your working notes here</p>
                        <p className="text-sm">Handwritten scratch pad - Image or PDF</p>
                      </div>
                    )}
                  </label>
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={uploading || !selectedTest || !scantronFile}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-6"
                  data-testid="submit-btn"
                  aria-label={uploading ? "Processing your submission" : "Submit answers for grading"}
                  aria-busy={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin mr-2" aria-hidden="true" />
                      Processing with AI...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" aria-hidden="true" />
                      Submit for Grading
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right Column */}
          <div className="lg:col-span-7 space-y-8">
            {/* Score Trend Chart */}
            {stats?.score_trend && stats.score_trend.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card data-testid="score-trend-chart">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Score Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64" role="img" aria-label="Line chart showing score progression over time">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={stats.score_trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis 
                            dataKey="name" 
                            tick={{ fontSize: 12, fill: '#4B5563' }}
                          />
                          <YAxis 
                            domain={[0, 100]}
                            tick={{ fontSize: 12, fill: '#4B5563' }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              fontFamily: 'JetBrains Mono, monospace',
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="percentage" 
                            stroke="#4338ca" 
                            strokeWidth={3}
                            dot={{ fill: '#4338ca', strokeWidth: 2, r: 4 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Weakness Analysis */}
            {sortedWeaknesses.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Card data-testid="weakness-analysis">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <Brain className="w-5 h-5 text-amber-600" aria-hidden="true" />
                      Areas to Improve
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4" role="list" aria-label="Topics needing improvement">
                      {sortedWeaknesses.slice(0, 5).map(([concept, count], index) => {
                        const maxCount = sortedWeaknesses[0][1];
                        const percentage = (count / maxCount) * 100;
                        return (
                          <div key={concept} className="space-y-2" role="listitem">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-gray-800">{concept}</span>
                              <span className="text-sm text-gray-600 font-mono">{count} questions to review</span>
                            </div>
                            <Progress value={percentage} className="h-2" aria-label={`${concept}: ${count} mistakes`} />
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Recent Submissions */}
            {stats?.recent_submissions && stats.recent_submissions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Card data-testid="recent-submissions">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Recent Tests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3" role="list" aria-label="Recent test submissions">
                      {stats.recent_submissions.map((sub) => {
                        // WCAG: Determine status label by score, not just color
                        const statusLabel = sub.percentage >= 70 ? 'Great!' : sub.percentage >= 50 ? 'Good' : 'Practice';
                        return (
                          <div
                            key={sub.submission_id}
                            className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            data-testid={`submission-${sub.submission_id}`}
                            role="listitem"
                          >
                            <button
                              onClick={() => navigate(`/submission/${sub.submission_id}`)}
                              className="flex-1 flex items-center gap-4 text-left focus-visible:ring-2 focus-visible:ring-primary rounded"
                              aria-label={`${sub.answer_key_name || "Test"}: ${sub.score} out of ${sub.total} correct, ${sub.percentage}%. ${statusLabel}`}
                            >
                              <div className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center ${
                                sub.percentage >= 70 ? 'bg-green-100 text-green-700' :
                                sub.percentage >= 50 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                <span className="font-bold font-mono text-sm">{sub.percentage}%</span>
                                <span className="text-xs">{statusLabel}</span>
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-gray-900">{sub.answer_key_name || "Test"}</p>
                                <p className="text-sm text-gray-600 font-mono">
                                  {sub.score}/{sub.total} correct
                                </p>
                              </div>
                            </button>
                            <div className="flex items-center gap-2">
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Submission?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete this submission. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={(e) => handleDeleteSubmission(sub.submission_id, e)}
                                      disabled={deletingId === sub.submission_id}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      {deletingId === sub.submission_id ? "Deleting..." : "Delete"}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                              <ChevronRight className="w-5 h-5 text-gray-400" aria-hidden="true" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Empty state for new students */}
            {(!stats?.recent_submissions || stats.recent_submissions.length === 0) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card>
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tests Yet</h3>
                    <p className="text-gray-600 max-w-sm mx-auto">
                      Upload your first answer sheet to get started! Select a test from the dropdown and upload your scantron.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
